import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Heart, MessageCircle, Reply, UserPlus, Check, MessagesSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";

type Notification = {
  id: string;
  type: "like" | "comment" | "reply" | "follow" | "post_like" | "post_comment" | "post_reply";
  actor_id: string;
  kit_id: string | null;
  post_id?: string | null;
  comment_id: string | null;
  is_read: boolean;
  created_at: string;
  actor?: { display_name: string | null; username: string | null; avatar_url: string | null };
  kit?: { id: string; club: string; season: string };
};

const typeIcon: Record<Notification["type"], JSX.Element> = {
  like: <Heart className="h-3.5 w-3.5 fill-destructive text-destructive" />,
  comment: <MessageCircle className="h-3.5 w-3.5 text-primary" />,
  reply: <Reply className="h-3.5 w-3.5 text-primary" />,
  follow: <UserPlus className="h-3.5 w-3.5 text-primary" />,
  post_like: <Heart className="h-3.5 w-3.5 fill-destructive text-destructive" />,
  post_comment: <MessagesSquare className="h-3.5 w-3.5 text-primary" />,
  post_reply: <Reply className="h-3.5 w-3.5 text-primary" />,
};

const typeText = (n: Notification) => {
  const name = n.actor?.username ? n.actor.username : "Ktoś";
  const kit = n.kit ? `${n.kit.club} ${n.kit.season}` : "Twoją koszulkę";
  switch (n.type) {
    case "like": return <><b>{name}</b> polubił(a) {kit}</>;
    case "comment": return <><b>{name}</b> skomentował(a) {kit}</>;
    case "reply": return <><b>{name}</b> odpowiedział(a) na Twój komentarz</>;
    case "follow": return <><b>{name}</b> zaczął(ęła) Cię obserwować</>;
    case "post_like": return <><b>{name}</b> polubił(a) Twój post</>;
    case "post_comment": return <><b>{name}</b> skomentował(a) Twój post</>;
    case "post_reply": return <><b>{name}</b> odpowiedział(a) na Twój komentarz pod postem</>;
  }
};

const linkFor = (n: Notification) => {
  if (n.type === "follow") return `/profile/${n.actor_id}`;
  if (n.type === "post_like" || n.type === "post_comment" || n.type === "post_reply") {
    if (!n.post_id) return "/pogadajmy";
    const params = new URLSearchParams();
    if (n.comment_id && (n.type === "post_comment" || n.type === "post_reply")) {
      params.set("comment", n.comment_id);
    }
    const qs = params.toString();
    return qs ? `/pogadajmy/${n.post_id}?${qs}` : `/pogadajmy/${n.post_id}`;
  }
  if (n.kit_id) return `/kit/${n.kit_id}`;
  return "#";
};

const PAGE_SIZE = 15;

export default function NotificationsBell() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const channelName = useMemo(
    () => `notifications-bell-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );

  const enrichRows = async (rows: any[]) => {
    const actorIds = [...new Set(rows.map((n) => n.actor_id))];
    const kitIds = [...new Set(rows.map((n) => n.kit_id).filter(Boolean))];
    const [actorsRes, kitsRes] = await Promise.all([
      actorIds.length
        ? supabase.from("profiles").select("user_id, display_name, username, avatar_url").in("user_id", actorIds)
        : Promise.resolve({ data: [] as any[], error: null }),
      kitIds.length
        ? supabase.from("kits").select("id, club, season").in("id", kitIds)
        : Promise.resolve({ data: [] as any[], error: null }),
    ]);
    if (actorsRes.error || kitsRes.error) {
      console.error("Failed to enrich notifications", actorsRes.error || kitsRes.error);
    }
    const actorMap = new Map((actorsRes.data || []).map((p: any) => [p.user_id, p]));
    const kitMap = new Map((kitsRes.data || []).map((k: any) => [k.id, k]));
    return rows.map((n: any) => ({
      ...n,
      actor: actorMap.get(n.actor_id),
      kit: n.kit_id ? kitMap.get(n.kit_id) : undefined,
    }));
  };

  const load = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, type, actor_id, kit_id, post_id, comment_id, is_read, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);

      if (error) {
        console.error("Failed to load notifications", error);
        setNotifications([]);
        setUnreadCount(0);
        setHasMore(false);
        return;
      }

      if (!data) return;

      const enriched = await enrichRows(data);
      setNotifications(enriched);
      setUnreadCount(enriched.filter((n) => !n.is_read).length);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      console.error("NotificationsBell crashed while loading", error);
      setNotifications([]);
      setUnreadCount(0);
      setHasMore(false);
    }
  };

  const loadMore = async () => {
    if (!user || loadingMore || notifications.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = notifications[notifications.length - 1];
      const { data, error } = await supabase
        .from("notifications")
        .select("id, type, actor_id, kit_id, post_id, comment_id, is_read, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .lt("created_at", oldest.created_at)
        .limit(PAGE_SIZE);
      if (error) {
        console.error("Failed to load more notifications", error);
        return;
      }
      const rows = data || [];
      const enriched = await enrichRows(rows);
      setNotifications((prev) => [...prev, ...enriched]);
      setHasMore(rows.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user) { setNotifications([]); setUnreadCount(0); return; }
    load();
    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName, user]);

  const markAllRead = async () => {
    if (!user || unreadCount === 0) return;
    await supabase.from("notifications").update({ is_read: true }).eq("user_id", user.id).eq("is_read", false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (v && unreadCount > 0) markAllRead();
  };

  if (!user) return null;

  const trigger = (
    <Button variant="ghost" size="sm" className="relative gap-1.5">
      <Bell className="h-4 w-4" />
      {unreadCount > 0 && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </Button>
  );

  const header = (
    <div className="flex items-center justify-between border-b p-3">
      <h3 className="font-semibold text-sm">Powiadomienia</h3>
      {notifications.some((n) => !n.is_read) && (
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={markAllRead}>
          <Check className="h-3 w-3" /> Oznacz przeczytane
        </Button>
      )}
    </div>
  );

  const list = (
    notifications.length === 0 ? (
      <p className="p-6 text-center text-sm text-muted-foreground">Brak powiadomień</p>
    ) : (
      <>
        <ul className="divide-y">
          {notifications.map((n) => (
            <li key={n.id} className={!n.is_read ? "bg-primary/5" : ""}>
              <Link
                to={linkFor(n)}
                onClick={() => setOpen(false)}
                className="flex items-start gap-3 p-3 transition-colors hover:bg-muted/60"
              >
                <div className="relative">
                  <Avatar className="h-9 w-9">
                    {n.actor?.avatar_url && <AvatarImage src={n.actor.avatar_url} />}
                    <AvatarFallback className="text-[10px] bg-muted">
                      {(n.actor?.username || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-background border">
                    {typeIcon[n.type]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-snug">{typeText(n)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: pl })}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        {hasMore && (
          <div className="p-3 flex justify-center">
            <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Ładowanie..." : "Załaduj starsze"}
            </Button>
          </div>
        )}
      </>
    )
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <div className="pt-2">{header}</div>
          <div className="flex-1 overflow-y-auto overscroll-contain">{list}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} collisionPadding={8} className="w-[420px] max-w-[calc(100vw-16px)] p-0">
        {header}
        <div className="max-h-[min(70vh,520px)] overflow-y-auto overscroll-contain">{list}</div>
      </PopoverContent>
    </Popover>
  );
}
